import smartDate from "@qrohlf/smart-date";
import { CircleSpinner } from "common/spinners";
import { StandardWidthPage } from "common/StandardWidthPage";
import { CTA } from "common/styleguide/CTA";
import { Hero } from "common/styleguide/Hero";
import { LoginModalOverlay } from "common/styleguide/LoginModalOverlay";
import { UnderlineSelector } from "common/UnderlineSelector";
import { Map } from "immutable";
import {
  inviteToGoal,
  joinGoal,
  leaveGoal,
  loadInvitedForGoal,
  loadParticipantsForGoal,
  selectParticipantsForGoal,
} from "lib/api/goalParticipants";
import { deleteGoal, loadGoal, saveGoal, updateGoal } from "lib/api/goals";
import { commonNouns } from "lib/localization";
import { loadStatus } from "lib/promiseMiddlewareHelpers";
import { contentLabel } from "lib/styleguide/base.scss";
import * as urlHelpers from "lib/urlHelpers";
import { goalDetails } from "lib/utils";
import React from "react";
import { defineMessages, FormattedMessage as T, injectIntl } from "react-intl";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { EditGoalModal } from "../EditGoalModal";
import { ExpandoCardContainer } from "../GoalCards/ExpandoCard";
import styles from "./GoalShowPage.scss";
import { InviteButton } from "./InviteButton";

const UNSCOPED = "unscoped";
const FRIENDS = "friends";
const LEADERBOARD_NUM_TO_SHOW = 100;
const PARTICIPANTS_NUM_TO_SHOW = 10;

const componentMessages = defineMessages({
  filterAll: {
    id: "GoalShowPage.filterAll",
    defaultMessage: "all",
    description: "option which will show all goal participants",
  },
  filterFriends: {
    id: "GoalShowPage.filterFriends",
    defaultMessage: "friends",
    description: "option which will filter goal leaderboard to only show friends",
  },
});

export class GoalShowPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      filter: UNSCOPED,
      showLogin: false,
    };
  }

  componentDidMount() {
    const {
      goal,
      match: { params },
      loadGoal,
      loadParticipantsForGoal,
      loadInvitedForGoal,
      currentUser,
    } = this.props;
    const goalId = parseInt(params.goalId);

    if (!goal) {
      // this guarantees that the current user's unscoped GP will get loaded
      loadGoal(goalId);
    }

    // always reload ranks on mount
    loadParticipantsForGoal(goalId);
    if (currentUser.id) {
      loadInvitedForGoal(goalId);
      loadParticipantsForGoal(goalId, { friends: true });
    }
  }

  joinGoal = () => {
    const { goal, joinGoal, currentUser } = this.props;
    if (currentUser.id) {
      joinGoal(goal.get("id"));
    } else {
      this.setState({ showLogin: true });
    }
  };

  hideLogin = () => {
    this.setState({ showLogin: false });
  };

  setFilter = (filter) => this.setState({ filter });

  inviteFriends = (friends) => {
    // friends is an [id: Map()] immutable Map of friend data
    const { goal, inviteToGoal } = this.props;
    inviteToGoal(goal.get("id"), friends.keySeq().toJS());
  };

  onDeleteGoal = () => {
    const { deleteGoal, goal, history } = this.props;
    // TODO build a nicer confirmation dialog
    if (window.confirm(`Are you sure you want to delete your goal, '${goal.get("name")}'?`)) {
      deleteGoal(goal.get("id")).then(() => history.push("/goals"));
    }
  };

  onLeaveGoal = () => {
    const { participation, leaveGoal } = this.props;
    leaveGoal(participation.get("id"));
  };

  onCloseEditModal = () => {
    const { goal, history } = this.props;
    const goalURL = `/goals/${goal.get("id")}`;
    history.replace(goalURL);
  };

  onEditGoal = (e) => {
    const { goal, history } = this.props;
    e.preventDefault();
    history.replace(`/goals/${goal.get("id")}/edit`);
  };

  render() {
    const { goal, editing, participants, participantsFriends, participation, currentUser, invited, metricUnits, intl } =
      this.props;
    const { filter, showLogin } = this.state;
    const friendsFirst = participantsFriends
      .toIndexedSeq()
      .sortBy((gp) => gp.get("rank"))
      .concat(
        participants
          .filterNot((p) => participantsFriends.has(p.get("id")))
          .toIndexedSeq()
          .sortBy((gp) => gp.get("rank"))
      );
    const leaderboardList = filter === FRIENDS ? participantsFriends : participants;

    if (!goal || goal.get("loadStatus") !== loadStatus.fulfilled) {
      return <CircleSpinner className={styles.loadingSpinner} centered size={50} />;
    }

    const imgSrc = goal && (typeof goal.get("icon") === "string" ? goal.get("icon") : goal.get("icon").preview);

    const showEditButton = participation && goal && goal.get("current_user_is_admin");
    const inviteButton = (
      <InviteButton
        type="default"
        invited={invited.merge(participants)}
        onConfirmInvites={this.inviteFriends}
        goalId={goal.get("id")}
      />
    );

    return (
      <div className={styles.GoalShowPage}>
        <Hero
          blur
          className={styles.hero}
          scrimOpacity={[0.3, 0.45]}
          label={intl.formatMessage(commonNouns.goal)}
          img={imgSrc || "/path/to/topo"}
          img2x={imgSrc}
        >
          <img className={styles.goalImg} height="100" width="100" src={imgSrc} />
          <h1>{goal.get("name")}</h1>
          <div className={styles.dateRange}>
            {smartDate([goal.get("starts_on"), goal.get("ends_on")].filter((d) => d))}
          </div>
        </Hero>
        <StandardWidthPage>
          <div className={styles.flex}>
            <LeftSidebar friendsFirst={friendsFirst} invited={invited} />
            <div className={styles.mainContent}>
              <HeadingWithAction
                heading={
                  <T
                    id="GoalShowPage.goalDetails"
                    defaultMessage="Goal Details"
                    description="heading for a textual description of the goal critera"
                  />
                }
                action={
                  showEditButton && (
                    <div>
                      <a onClick={this.onEditGoal}>
                        <T id="GoalShowPage.editGoal" defaultMessage="Edit Goal" />
                      </a>{" "}
                      <a className={styles.deleteLink} onClick={this.onDeleteGoal}>
                        <T id="GoalShowPage.deleteGoal" defaultMessage="Delete Goal" />
                      </a>
                    </div>
                  )
                }
              />
              <p>{goalDetails(goal, metricUnits, intl)}</p>

              {!participation && (
                <CTA margins={false} onClick={this.joinGoal}>
                  Join Goal
                </CTA>
              )}

              {participation && (
                <HeadingWithAction
                  heading={<T id="GoalShowPage.yourProgress" defaultMessage="Your Progress" />}
                  action={
                    goal.get("current_user_is_admin") ? (
                      inviteButton
                    ) : (
                      <a onClick={this.onLeaveGoal}>
                        <T id="GoalShowPage.leaveGoal" defaultMessage="Leave Goal" />
                      </a>
                    )
                  }
                />
              )}

              <div className={styles.gpCardContainer}>
                {participation && (
                  <ExpandoCardContainer goalParticipant={participation} goal={participation.get("goal")} />
                )}
              </div>

              <div className={styles.flexHeading}>
                <h2>
                  <T id="GoalShowPage.leaderboard" defaultMessage="Leaderboard" />
                </h2>
                <div>
                  <T id="GoalShowPage.filter" defaultMessage="filter:" />{" "}
                  <UnderlineSelector
                    options={Map({
                      [UNSCOPED]: intl.formatMessage(componentMessages.filterAll),
                      [FRIENDS]: intl.formatMessage(componentMessages.filterFriends),
                    })}
                    selected={filter}
                    onSelectionChanged={this.setFilter}
                  />
                </div>
              </div>
              {leaderboardList
                .sortBy((gp) => gp.get("rank"))
                .take(LEADERBOARD_NUM_TO_SHOW)
                .map((gp) => (
                  <ExpandoCardContainer
                    key={gp.get("id")}
                    goalParticipant={gp}
                    goal={goal}
                    highlight={gp.getIn(["user", "id"]) === currentUser.id}
                  />
                ))
                .toIndexedSeq()}
              {leaderboardList.size > LEADERBOARD_NUM_TO_SHOW && (
                <p className={styles.sizeHint}>...and {leaderboardList.size - LEADERBOARD_NUM_TO_SHOW} others</p>
              )}
            </div>
          </div>
        </StandardWidthPage>
        {editing && goal && (
          <EditGoalModal
            goal={goal}
            saveGoal={this.props.saveGoal}
            onRequestClose={this.onCloseEditModal}
            metricUnits={metricUnits}
          />
        )}
        {showLogin && <LoginModalOverlay onRequestClose={this.hideLogin} redirectTo={window.location.pathname} />}
      </div>
    );
  }
}

const HeadingWithAction = ({ heading, action }) => (
  <div className={styles.headingActions}>
    <h2>{heading}</h2>
    {action && <div>{action}</div>}
  </div>
);

const LeftSidebar = ({ friendsFirst, invited }) => (
  <div className={styles.leftCol}>
    <h3 className={contentLabel}>
      <T
        id="GoalShowPage.participating"
        defaultMessage="Participating:"
        description="Section label for the list of users participating in a goal"
      />
    </h3>
    <div className={styles.participantList}>
      {friendsFirst.take(PARTICIPANTS_NUM_TO_SHOW).map((gp) => (
        <a className={styles.participantListItem} href={`/users/${gp.getIn(["user", "id"])}`} key={gp.get("id")}>
          <img
            title={gp.getIn(["user", "name"])}
            alt={gp.getIn(["user", "name"])}
            src={urlHelpers.photoMilli(gp.getIn(["user", "highlighted_photo_id"]))}
          />
        </a>
      ))}
    </div>
    {friendsFirst.size > PARTICIPANTS_NUM_TO_SHOW && (
      <p className={styles.sizeHint}>...and {friendsFirst.size - PARTICIPANTS_NUM_TO_SHOW} others</p>
    )}
    {invited.size > 0 && (
      <div>
        <h3 className={contentLabel}>Invited:</h3>
        <div className={styles.participantList}>
          {invited.toIndexedSeq().map((gp) => (
            <a href={`/users/${gp.getIn(["user", "id"])}`} key={gp.get("id")}>
              <img
                className={styles.participantListImg}
                title={gp.getIn(["user", "name"])}
                alt={gp.getIn(["user", "name"])}
                src={urlHelpers.photoMilli(gp.getIn(["user", "highlighted_photo_id"]))}
              />
            </a>
          ))}
        </div>
      </div>
    )}
  </div>
);

const mapStateToProps = (state, ownProps) => {
  const goalId = parseInt(ownProps.match.params.goalId);
  const participants = selectParticipantsForGoal(state, goalId);

  return {
    goal: goalId && state.goals.get(goalId),
    gpLoaded: state.goalsForUser.get("loaded"),
    participants,
    participantsFriends: selectParticipantsForGoal(state, goalId, {
      friends: true,
    }),
    participation: participants.find((p) => p.getIn(["user", "id"]) === state.currentUser.id),
    invited: state.goalParticipants.invited.filter((p) => p.getIn(["goal", "id"]) === goalId),
    currentUser: state.currentUser,
    metricUnits: state.currentUser && state.currentUser.preferences.get("metric_units"),
  };
};

const mapDispatchToProps = {
  loadGoal,
  updateGoal,
  saveGoal,
  deleteGoal,
  joinGoal,
  loadParticipantsForGoal,
  inviteToGoal,
  loadInvitedForGoal,
  leaveGoal,
};

export const GoalShowPageContainer = injectIntl(connect(mapStateToProps, mapDispatchToProps)(withRouter(GoalShowPage)));
